/**
 * Format a list by given formatter function
 * @param {Array} list - Origin list to format
 * @param {function} formatterFn - Function to format each item and returns it
 *
 * @return {Array}
 */
export const formatBy = (list = [], formatterFn = (v) => v) =>
  list.map(formatterFn);

/**
 * Format professor item just to get necessery info
 * @param {Professor} profItem - Professor object returned from iums
 *
 * @return {Object} Formatted professor info
 */
export const fProf = (profItem) => {
  if (!profItem) return null;

  const formatted = {
    id: profItem.empId,
    firstName: profItem.firstName,
    photo: profItem.photo,
    lastName: profItem.lastName,
    email: profItem.email,
    phone: profItem.phone,
    researchFields: profItem.employeeResearchInformationDTOS?.map((v) => ({
      title: v?.title,
      effectiveDate: v?.effectiveDate,
      type: v?.type
    })),
    positions: profItem.employeePositionDTOS ?? [],
    experiences: profItem.employeeExperienceDTOS ?? [],
    educations: profItem.employeeEducationDTOS?.map((v) => ({
      educationalMajor: v?.educationalMajor,
      educationalSegment: v?.educationalSegment,
      institute: v?.institute
    }))
  };

  return formatted;
};

/**
 * Format orientation of group item so as to get necessery formatted data
 * @param {OrientationItem} oriItem - Orientation item of GroupDetail object returned from iums
 *
 * @return {Object} Formatted orientation info
 */
export const fCourse = (courseInfo) => {
  if (!courseInfo) return null;

  const unitsSummer = (...keys) =>
    keys.reduce((t, k) => (courseInfo[k] ? t + courseInfo[k] : t), 0);

  const formatted = {
    title: courseInfo.course.title,
    category: courseInfo.courseCategory?.title,
    unitType: courseInfo.unitType?.title,
    code: courseInfo.code,
    // Units Count
    projectUnitNo: courseInfo?.projectUnitNo ?? 0,
    laboratoryUnitNo: courseInfo?.laboratoryUnitNo ?? 0,
    workshopUnitNo: courseInfo?.workshopUnitNo ?? 0,
    theoreticalUnitNo: courseInfo?.theoreticalUnitNo ?? 0,
    totalUnit: unitsSummer(
      'projectUnitNo',
      'laboratoryUnitNo',
      'workshopUnitNo',
      'theoreticalUnitNo'
    )
  };

  return formatted;
};

/**
 * Format Scheduled Semester Course so as to get necessery formatted data
 * @param {CourseSchedule} scheduleItem - Orientation item of GroupDetail object returned from iums
 *
 * @return {Object} Formatted scheduled course info
 */
export const fScheduleItem = (scheduleItem) => {
  if (!scheduleItem) return null;

  const formatted = {
    title: scheduleItem.title,
    id: scheduleItem.id,
    examDate: scheduleItem.examDate,
    startExamTime: scheduleItem.startExamTime,
    endExamTime: scheduleItem.endExamTime,
    examRoundType: scheduleItem.examRoundType,
    // Preq and Req list
    prerequisitesCourses: scheduleItem.prerequisitesCourses ?? [],
    requisitesCourses: scheduleItem.requisitesCourses ?? [],
    // Other detail
    isMain: scheduleItem.presentationCourseDTOS?.[0]?.isMain,
    professor:
      scheduleItem.presentationCourseDTOS?.[0]?.presentationDTO
        ?.classSpecificationDTOS?.[0]?.employee,
    startTime:
      scheduleItem.presentationCourseDTOS?.[0]?.presentationDTO
        ?.classSpecificationDTOS?.[0]?.startTime,
    endTime:
      scheduleItem.presentationCourseDTOS?.[0]?.presentationDTO
        ?.classSpecificationDTOS?.[0]?.endTime,
    methodPresentation:
      scheduleItem.presentationCourseDTOS?.[0]?.presentationDTO
        ?.classSpecificationDTOS?.[0]?.methodPresentation,
    presentationDay:
      scheduleItem.presentationCourseDTOS?.[0]?.presentationDTO
        ?.classSpecificationDTOS?.[0]?.presentationDay,
    location:
      scheduleItem.presentationCourseDTOS?.[0]?.presentationDTO
        ?.classSpecificationDTOS?.[0]?.location
  };

  return formatted;
};

/**
 * Format orientation of group item so as to get necessery formatted data
 * @param {OrientationItem} oriItem - Orientation item of GroupDetail object returned from iums
 *
 * @return {Object} Formatted orientation info
 */
export const fOrientation = (oriItem) => {
  if (!oriItem) return null;
  return oriItem?.termPlanningDTOS?.map((item, i) => ({
    type: oriItem.type,
      title: oriItem.title,
      code: oriItem.code,
      segmentAndSegmentType: item.segmentAndSegmentType,
      planningList: item?.scheduledSemesterDTOS?.map(
        (v) => ({
          title: v?.title,
          courses: formatBy(v.courses ?? [], fScheduleItem)
        })
      ),
      coursesList:
        formatBy(
          item?.educationalStructureCourseList ?? [],
          fCourse
        ) ?? []
  }));
};

/**
 * Format groupDetail item just to get necessery info
 * @param {GroupDetail} groupInfoList - GroupDetail object returned from iums
 *
 * @return {Object} Formatted group info containing its orientations
 */
export const fGroupInfo = (groupInfoList) => {
  if (!groupInfoList || !Array.isArray(groupInfoList)) return null;

  const formatted = {
    title: 'عنوان گروه',
    orientations: formatBy(groupInfoList, fOrientation)
  };

  return formatted;
};

export default {
  formatBy,
  fProf
};

export const MONTH_MAP = {
  'Farvardin': 'فروردین',
  'Ordibehesht': 'اردیبهشت',
  'Khordaad': 'خرداد',
  'Tir': 'تیر',
  'Amordaad': 'مرداد',
  'Shahrivar': 'شهریور',
  'Mehr': 'مهر',
  'Aabaan': 'آبان',
  'Aazar': 'آذر',
  'Dey': 'دی',
  'Bahman': 'بهمن',
  'Esfand': 'اسفند'
}

export const DAY_MAP = {
  "Saturday": "شنبه",
  "Sunday": "یکشنبه",
  "Monday": "دوشنبه",
  "Tuesday": "سه شنبه",
  "Wednesday": "چهارشنبه",
  "Thursday": "پنجشنبه",
  "Friday": "جمعه",
}